import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useCart } from '@/features/cart/context/CartContext';
import { useMarketplace } from '../context/marketplaceContext';
import '../marketplace.css';
import ProductCard from './ProductCard';
import StorefrontHero, {
  getStorefrontMapUrl,
  StorefrontLoading,
  StorefrontNotFound,
} from './StorefrontHero';

const SORT_OPTIONS = [
  { key: 'default', label: 'مرتب‌سازی پیش‌فرض' },
  { key: 'price-asc', label: 'قیمت: کم به زیاد' },
  { key: 'price-desc', label: 'قیمت: زیاد به کم' },
];
const EMPTY_PRODUCTS = [];

export function VendorProductGrid({ products = EMPTY_PRODUCTS, emptyMessage = 'این فروشگاه هنوز محصولی ندارد.' }) {
  const { cartWarning } = useCart();
  const [sortBy, setSortBy] = useState('default');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : EMPTY_PRODUCTS),
    [products],
  );

  const categories = useMemo(() => {
    const byId = new Map();
    safeProducts.forEach((product) => {
      (product.categories || []).forEach((category) => {
        byId.set(category.id, category.name_fa);
      });
    });
    return Array.from(byId, ([id, name_fa]) => ({ id, name_fa }));
  }, [safeProducts]);

  const visibleProducts = useMemo(() => {
    let list = safeProducts;
    const query = search.trim().toLocaleLowerCase();
    if (query) list = list.filter((product) => product.name_fa?.toLocaleLowerCase().includes(query));
    if (categoryFilter !== 'all') {
      list = list.filter((product) =>
        (product.categories || []).some((category) => category.id === Number(categoryFilter))
      );
    }
    if (sortBy === 'price-asc') {
      list = [...list].sort((a, b) => Number(a.base_price) - Number(b.base_price));
    } else if (sortBy === 'price-desc') {
      list = [...list].sort((a, b) => Number(b.base_price) - Number(a.base_price));
    }
    return list;
  }, [safeProducts, categoryFilter, search, sortBy]);

  const hasFilters = Boolean(search.trim()) || categoryFilter !== 'all' || sortBy !== 'default';

  return (
    <section className="vendor-products-section">
      {cartWarning && <p className="vendor-products-cart-warning" role="alert">{cartWarning}</p>}
      <div className="vendor-products-toolbar">
        <label className="vendor-products-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="جست‌وجو در محصولات..."
            aria-label="جست‌وجو در محصولات فروشگاه"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} aria-label="پاک کردن جست‌وجو">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
        </label>
        {categories.length > 1 && (
          <label className="vendor-products-select-wrap">
            <select
              aria-label="دسته‌بندی"
              className="vendor-products-select"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="all">همه دسته‌ها</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name_fa}</option>
              ))}
            </select>
            <i className="fas fa-chevron-down" aria-hidden="true" />
          </label>
        )}
        {safeProducts.length > 1 && (
          <label className="vendor-products-select-wrap">
            <select
              aria-label="مرتب‌سازی"
              className="vendor-products-select"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
            <i className="fas fa-chevron-down" aria-hidden="true" />
          </label>
        )}
      </div>

      <div className="vendor-products-results">
        <span>{visibleProducts.length.toLocaleString('fa-IR')} محصول</span>
        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setCategoryFilter('all');
              setSortBy('default');
            }}
          >
            پاک کردن فیلترها
          </button>
        )}
      </div>

      {visibleProducts.length ? (
        <div className="vendor-products-grid">
          {visibleProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="vendor-products-empty">
          <i className="fas fa-box-open" aria-hidden="true" />
          <p>{hasFilters ? 'محصولی با این مشخصات پیدا نشد.' : emptyMessage}</p>
        </div>
      )}
    </section>
  );
}

const TABS = [
  { key: 'products', label: 'محصولات' },
  { key: 'about', label: 'درباره فروشگاه' },
];

function VendorAboutPanel({ storefront, productCount }) {
  const paragraphs = (storefront.vendor_history_fa || '')
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const facts = [
    { icon: 'fa-store', label: 'نام فروشگاه', value: storefront.vendor_shop_name_fa },
    { icon: 'fa-box-open', label: 'تعداد محصولات', value: productCount.toLocaleString('fa-IR') },
    storefront.is_verified && { icon: 'fa-check-circle', label: 'وضعیت', value: 'فروشنده تأییدشده' },
  ].filter(Boolean);

  const hasPin = storefront.latitude != null && storefront.longitude != null;
  const mapUrl = getStorefrontMapUrl(storefront);
  const hasContact = storefront.vendor_local_phone || storefront.address || hasPin;

  return (
    <div className="vendor-about-layout">
      <div className={`vendor-about${paragraphs.length ? '' : ' no-story'}`}>
        {paragraphs.length > 0 && (
          <article className="vendor-about-story">
            <h2 className="vendor-about-heading">
              <i className="fas fa-book-open" aria-hidden="true" /> تاریخچهٔ فروشگاه
            </h2>
            {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </article>
        )}

        <aside className="vendor-about-facts" aria-label="اطلاعات فروشگاه">
          {facts.map((fact) => (
            <div className="vendor-about-fact" key={fact.label}>
              <span className="vendor-about-fact-icon" aria-hidden="true">
                <i className={`fas ${fact.icon}`} />
              </span>
              <div>
                <span className="vendor-about-fact-label">{fact.label}</span>
                <span className="vendor-about-fact-value">{fact.value}</span>
              </div>
            </div>
          ))}
        </aside>
      </div>

      {hasContact && (
        <section className="vendor-about-contact">
          <h2 className="vendor-about-heading">
            <i className="fas fa-address-book" aria-hidden="true" /> اطلاعات تماس
          </h2>
          <div className="vendor-contact">
            {storefront.vendor_local_phone && (
              <div className="vendor-contact-card">
                <span className="vendor-contact-icon" aria-hidden="true"><i className="fas fa-phone" /></span>
                <div className="vendor-contact-body">
                  <span className="vendor-contact-label">تلفن فروشگاه</span>
                  <span className="vendor-contact-value" dir="ltr">{storefront.vendor_local_phone}</span>
                </div>
                <a className="vendor-contact-action" href={`tel:${storefront.vendor_local_phone}`}>
                  <i className="fas fa-phone-alt" aria-hidden="true" /> تماس
                </a>
              </div>
            )}

            {(storefront.address || hasPin) && (
              <div className="vendor-contact-card">
                <span className="vendor-contact-icon" aria-hidden="true"><i className="fas fa-map-marker-alt" /></span>
                <div className="vendor-contact-body">
                  <span className="vendor-contact-label">آدرس</span>
                  <span className="vendor-contact-value">
                    {storefront.address || 'موقعیت روی نقشه ثبت شده است'}
                  </span>
                </div>
                {mapUrl && (
                  <a className="vendor-contact-action" href={mapUrl} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-directions" aria-hidden="true" /> مسیریابی
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default function VendorStorefront() {
  const { vendorId } = useParams();
  const { vendor, state: marketplaceState, isVendorSubdomain } = useMarketplace();
  const slugMode = isVendorSubdomain && !vendorId;
  const [storefront, setStorefront] = useState(null);
  const [idState, setIdState] = useState('loading');
  const [tabState, setTabState] = useState({ storefrontId: null, tab: 'products' });

  useEffect(() => {
    if (slugMode || !vendorId) return undefined;
    let cancelled = false;

    Promise.resolve().then(async () => {
      if (cancelled) return;
      setIdState('loading');
      try {
        const { data, error } = await apiClient.GET('/api/v1/vendors/{vendor_id}', {
          params: { path: { vendor_id: vendorId } },
        });
        if (cancelled) return;
        setStorefront(data ?? null);
        setIdState(error || !data ? 'error' : 'ready');
      } catch {
        if (cancelled) return;
        setStorefront(null);
        setIdState('error');
      }
    });

    return () => { cancelled = true; };
  }, [slugMode, vendorId]);

  const activeStorefront = slugMode ? vendor : storefront;
  const activeState = slugMode
    ? marketplaceState === 'loading'
      ? 'loading'
      : marketplaceState === 'not-found'
        ? 'error'
        : 'ready'
    : idState;

  const activeTab = tabState.storefrontId === activeStorefront?.id ? tabState.tab : 'products';

  if (activeState === 'loading') return <StorefrontLoading />;
  if (activeState === 'error' || !activeStorefront) return <StorefrontNotFound />;

  const productCount = activeStorefront.products?.length ?? 0;

  return (
    <main>
      {!slugMode && (
        <div className="storefront-back-wrap">
          <div className="container">
            <Link className="storefront-back" to="/marketplace">
              <i className="fas fa-arrow-right" aria-hidden="true" /> بازگشت به فروشندگان
            </Link>
          </div>
        </div>
      )}

      <StorefrontHero
        name={activeStorefront.vendor_shop_name_fa}
        banner={activeStorefront.vendor_banner_url}
        verified={activeStorefront.is_verified}
        productCount={productCount}
        address={activeStorefront.address}
        latitude={activeStorefront.latitude}
        longitude={activeStorefront.longitude}
        phone={activeStorefront.vendor_local_phone}
      />

      <div className="container storefront-content">
        <nav className="storefront-tabs" data-active-tab={activeTab} aria-label="بخش‌های فروشگاه">
          <span className="storefront-tab-indicator" aria-hidden="true" />
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`storefront-tab${activeTab === tab.key ? ' active' : ''}`}
              aria-pressed={activeTab === tab.key}
              onClick={() => setTabState({ storefrontId: activeStorefront.id, tab: tab.key })}
            >
              {tab.label}
              {tab.key === 'products' && (
                <span className="storefront-tab-count">{productCount.toLocaleString('fa-IR')}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="storefront-tab-content">
          {activeTab === 'products'
            ? <VendorProductGrid products={activeStorefront.products} />
            : <VendorAboutPanel storefront={activeStorefront} productCount={productCount} />}
        </div>
      </div>
    </main>
  );
}
